<script setup lang="ts">
// Persistent top-level chrome — not an explicit DESIGN.md screen, but the 5 screens need some
// way to get from one to another besides scanning/tapping the table. Hidden on Live match (full
// focus on the score, per DESIGN.md section 9.1 screen 3) and on New match (the table drawing
// already fills the viewport). Small enough to leave 960x540 uncluttered.
//
// Two stacked rows: a slim logo row on top, then the tabs row. Keeping the logo off the tabs
// row is what buys 2-word tab labels the horizontal room they need — see the
// mdi icons + smaller label text below. @mdi/js is a local npm dep (no icon-font CDN).
import { computed, ref } from 'vue'
import { mdiAccountMultiple, mdiHelpCircleOutline, mdiHistory, mdiHome, mdiTrophy } from '@mdi/js'
import { nav, navigate } from '../store/nav'
import { tableStore } from '../store/table'
import HelpOverlay from './HelpOverlay.vue'
import KomboLogo from './KomboLogo.vue'

// Round-4 feedback item 1: "Rankings" tab merged into "Players" — the Players page IS the
// ranking now (rank #, avatar, name, elo, W-L) plus a search field.
// Round-8 feedback item 21: "Tournaments" tab added — mdiTrophy freed up by the Rankings->Players
// merge above.
// Round-10 (2026-08-31): the Debug tab moved under Settings (SettingsPanel.vue) — it's a
// developer surface, not a tab players should see.
const tabs: { screen: 'home' | 'pastMatches' | 'players' | 'tournaments'; label: string; icon: string }[] = [
  { screen: 'home', label: 'Home', icon: mdiHome },
  { screen: 'pastMatches', label: 'Matches', icon: mdiHistory },
  { screen: 'players', label: 'Players', icon: mdiAccountMultiple },
  { screen: 'tournaments', label: 'Tournaments', icon: mdiTrophy },
]

const helpOpen = ref(false)

const activeTab = computed(() => {
  if (nav.screen === 'pastMatchDetail') return 'pastMatches'
  if (nav.screen === 'tournamentDetail') return 'tournaments'
  if (nav.screen === 'versus' || nav.screen === 'player') return 'players'
  return nav.screen
})
</script>

<template>
  <header class="shrink-0 flex flex-col border-b border-neutral-800 bg-neutral-950">
    <KomboLogo />
    <div class="h-9 shrink-0 flex items-center gap-2 px-3">
      <button
        v-for="t in tabs"
        :key="t.screen"
        type="button"
        class="h-8 min-h-0 px-3 rounded-lg text-[10px] font-semibold transition flex items-center gap-1.5"
        :class="activeTab === t.screen ? 'bg-emerald-600 text-white' : 'text-neutral-400 active:bg-white/5'"
        @click="navigate(t.screen)"
      >
        <svg viewBox="0 0 24 24" class="w-3.5 h-3.5 shrink-0" fill="currentColor"><path :d="t.icon" /></svg>
        {{ t.label }}
      </button>
      <div class="flex-1"></div>
      <div class="text-xs text-neutral-500 font-mono">{{ tableStore.table }}</div>
      <button
        type="button"
        class="h-8 min-h-0 w-8 min-w-0 shrink-0 rounded-full bg-neutral-800 active:bg-neutral-700 flex items-center justify-center text-neutral-300"
        aria-label="Help"
        @click="helpOpen = true"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="currentColor"><path :d="mdiHelpCircleOutline" /></svg>
      </button>
    </div>
  </header>

  <HelpOverlay :open="helpOpen" @dismiss="helpOpen = false" />
</template>
